import { has, upperFirst } from 'lodash/fp';
import { FinancialDocumentPdfTemplateType } from '@mews-commander/data-access';
import { CommanderThunkAction } from '@mews-commander/types';
import { openInBrowserTab, sendControllerRequest } from '@mews-commander/utils';
import { showErrorAlert } from '@mews/framework';
import { hidePageLoader, showPageLoader } from '../../../core/pageLoader';
import { navigateInModal, refreshPage } from '../../../modules/app/actions/common';
import { ApiGeneratedAlertType, showAlert } from '../../../modules/app/components/TemplateFetcher/utils';
import { getPlatformPrimaryUrl } from '../../../modules/app/reducers/configuration';

export interface PrintFunctionParams {
    printPageSize: string;
    printPageOrientation: string;
    languageCode: string;
    footerLeftText: string;
    footerCenterText: string;
    printerId: string;
    reason?: string;
    templateType: FinancialDocumentPdfTemplateType | '';
}

interface PrintFunctionResponse {
    message: string;
    type: ApiGeneratedAlertType;
}

const getPrintParams = ({
    printPageSize,
    printPageOrientation,
    languageCode,
    footerLeftText,
    footerCenterText,
    printerId,
    reason: printReason,
    templateType,
}: PrintFunctionParams) => {
    const searchParams = new URLSearchParams({
        printPageSize,
        printPageOrientation,
        languageCode,
        footerLeftText,
        footerCenterText,
        printerId,
        ...(printReason != null ? { printReason } : {}),
        templateType,
    });
    return searchParams.toString();
};

const getPrintUrl = (params: PrintFunctionParams, pdfUrl: string) => {
    const printParams = getPrintParams(params);
    return `${pdfUrl}&${printParams}`;
};

export const sendToCustomer =
    (printReason: string | undefined, url: string): CommanderThunkAction =>
    (dispatch) => {
        const sendUrl = `${url}&PrintReason=${printReason}`;

        return dispatch(navigateInModal(sendUrl));
    };

export const print =
    (params: PrintFunctionParams, pdfUrl: string, reloadOnPrint = false): CommanderThunkAction =>
    (dispatch) => {
        showPageLoader();

        const platformPrimaryUrl = getPlatformPrimaryUrl();
        const printUrl = getPrintUrl(params, pdfUrl);
        const url = `${platformPrimaryUrl}${printUrl}`;

        const response = sendControllerRequest(url);
        response
            .then((result: PrintFunctionResponse) => {
                hidePageLoader();

                if (has('message', result) && has('type', result)) {
                    return showAlert({
                        message: result.message,
                        type: upperFirst(result.type) as ApiGeneratedAlertType,
                    });
                }

                if (reloadOnPrint) {
                    dispatch(refreshPage());
                }
            })
            .catch(({ message }) => {
                hidePageLoader();

                showErrorAlert({ description: <>{message}</> });
            });
    };

export const showPdf =
    (params: PrintFunctionParams, pdfUrl: string, reloadOnPrint = false): CommanderThunkAction =>
    (dispatch) => {
        const url = getPrintUrl(params, pdfUrl);
        openInBrowserTab(url);
        if (reloadOnPrint) dispatch(refreshPage());
    };
